import React, {useEffect} from "react";
import {useAppSelector, useAppDispatch} from "../../utils/Hooks";
import { getLockedRecommendations } from "../../store/reducers/LockedRecommendationsSlice";
import { LockedRecommendationCountry, LockedRecommendationService } from "../../models/LockedRecommendation";
import {LoggedInView} from "../common/LoggedInView";
import { ModifyModal } from "./components/ModifyModal";
import { NewModal } from "./components/NewModal";
import {RequestStatus} from "../../utils/Enums";

interface LockedRecommendationsScreenProps {
}

export const LockedRecommendationsScreen: React.FC<LockedRecommendationsScreenProps> = () => {

    const { data, createRouteStatus, bulkUpdateStatus } = useAppSelector(state => state.lockedRecommendations) as any;
    const dispatch = useAppDispatch();
    const [selected, setSelected] = React.useState<number[]>([]);
    const [openModify, setOpenModify] = React.useState(false);
    const [openNew, setOpenNew] = React.useState(false);

    useEffect(() => {
        dispatch(getLockedRecommendations());
    }, [dispatch]);

    useEffect(() => {
        if (createRouteStatus === RequestStatus.Success || bulkUpdateStatus === RequestStatus.Success) {
            dispatch(getLockedRecommendations());
        }
    }, [createRouteStatus, bulkUpdateStatus, dispatch]);

    return (
        <LoggedInView fullWidth={false}>
            <div style={{marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center'}}>
                <button
                    style={{padding: '8px 14px', height: '38px', border: '1px solid #ccc', borderRadius: '6px', background: '#fff', cursor: 'pointer'}}
                    type="button"
                    onClick={() => setOpenNew(true)}
                >
                    Lisa uus
                </button>

                <button
                    style={{padding: '8px 14px', height: '38px', border: '1px solid #ccc', borderRadius: '6px', background: '#fff', cursor: 'pointer'}}
                    type="button"
                    disabled={selected.length === 0}
                    onClick={() => setOpenModify(true)}
                >
                    Muuda valituid ({selected.length})
                </button>
            </div>
            {data.map((service: LockedRecommendationService) => (
                <div key={service.service_id} style={{marginBottom: '30px'}}>
                    <h2>{service.service_name}</h2>

                    {service.countries.map((country: LockedRecommendationCountry) => (
                        <div key={country.start_country_id} style={{marginTop: '15px'}}>

                            <table className="data">
                                <thead>
                                <tr>
                                    <th style={{width: '5%'}}></th>
                                    <th style={{width: '20%'}}>Sihtkoht</th>
                                    <th>Kommentaar</th>
                                </tr>
                                </thead>
                                <tbody>
                                {Object.values(
                                    (country.comments ?? []).reduce((acc: any, c: any) => {
                                        const key = c.destination_country_id;
                                        if (!acc[key]) {
                                            acc[key] = { destination: c.destination_country, items: [] };
                                        }
                                        acc[key].items.push(c);
                                        return acc;
                                    }, {})
                                ).map((group: any) => {
                                    const hasPartial = group.items.some((i: any) => i.type === 'partial');
                                    const hasFull = group.items.some((i: any) => i.type === 'full');

                                    return group.items.map((comment: any) => (
                                        <tr key={comment.id}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selected.includes(comment.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelected([...selected, comment.id]);
                                                        } else {
                                                            setSelected(selected.filter(id => id !== comment.id));
                                                        }
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                <div>
                                                    {country.start_country_name} → {group.destination?.name}
                                                    {hasPartial && hasFull ? (comment.type === 'partial' ? ' (<13.6 ldm)' : ' (13.6 ldm)') : ''}
                                                </div>
                                                {comment.random_inquiry_id &&
                                                    <div style={{marginTop: 6}}>
                                                        <a
                                                            href={`/inquiries/${comment.random_inquiry_id}/recommended`}
                                                            style={{fontSize: 16}}
                                                        >
                                                            ⛟
                                                        </a>
                                                    </div>
                                                }
                                            </td>
                                            <td style={{whiteSpace: 'pre-wrap'}}>
                                                <span
                                                    dangerouslySetInnerHTML={{
                                                        __html: (comment.comment ?? '')
                                                            .replace(
                                                                /([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi,
                                                                '<a href="mailto:$1">$1</a>'
                                                            )
                                                            .replace(
                                                                /(https?:\/\/[^\s]+)/g,
                                                                '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
                                                            )
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    ));
                                })}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            ))}
            <div style={{marginTop: '20px', display: 'flex', gap: '10px'}} className="actions-bottom">
                <button style={{padding: '8px 14px', border: '1px solid #ccc', borderRadius: '6px', background: '#fff', cursor: 'pointer'}} type="button" onClick={() => setOpenNew(true)}>
                    Lisa uus
                </button>

                <button style={{padding: '8px 14px', border: '1px solid #ccc', borderRadius: '6px', background: '#fff', cursor: 'pointer'}} type="button" disabled={selected.length === 0} onClick={() => setOpenModify(true)}>
                    Muuda valituid ({selected.length})
                </button>
            </div>
            <NewModal
                open={openNew}
                onClose={() => setOpenNew(false)}
                onSaved={() => {
                    setOpenNew(false);
                }}
            />

            <ModifyModal
                open={openModify}
                ids={selected}
                initialComment={
                    (() => {
                        const allComments = data.flatMap((s: any) =>
                            s.countries.flatMap((c: any) =>
                                c.comments ?? (c.routes?.flatMap((r: any) => r.comments ?? []) ?? [])
                            )
                        );

                        const selectedComments = allComments.filter((c: any) => selected.includes(c.id));

                        return selectedComments[0]?.comment ?? '';
                    })()
                }
                initialLocked={
                    (() => {
                        const allComments = data.flatMap((s: any) =>
                            s.countries.flatMap((c: any) =>
                                c.comments ?? (c.routes?.flatMap((r: any) => r.comments ?? []) ?? [])
                            )
                        );

                        const selectedComments = allComments.filter((c: any) => selected.includes(c.id));

                        return !!selectedComments[0]?.locked;
                    })()
                }
                onClose={() => setOpenModify(false)}
                onSaved={() => {
                    setOpenModify(false);
                    setSelected([]);
                }}
            />
        </LoggedInView>
    );
}